import { Color } from "../../../../maths/Color";
import { FastSinglelist } from "../../../../utils/SingletonList";
import { RenderState2D } from "../../../../webgl/utils/RenderState2D";
import { LayaGL } from "../../../../layagl/LayaGL";
import { Vector2 } from "../../../../maths/Vector2";
import { ShaderDefines2D } from "../../../../webgl/shader/d2/ShaderDefines2D";
import { Matrix } from "../../../../maths/Matrix";
import { Vector3 } from "../../../../maths/Vector3";
import { SpriteUtils } from "../../../../utils/SpriteUtils";
import { BatchManager } from "./BatchManager";
import { LayaEnv } from "../../../../../LayaEnv";
import { BaseRender2DType } from "../../../../display/SpriteConst";
import { Pool } from "../../../../utils/Pool";
import { NodeFlags } from "../../../../Const";
import { WebGraphicsBatch } from "./WebGraphicsBatch";
BatchManager.registerProvider(BaseRender2DType.graphics, WebGraphicsBatch);
class SortedStructs {
    constructor() {
        this.lists = new Map();
        this._indice = new Set;
        this._sortedIndice = [];
    }
    add(struct) {
        let list = this.lists.get(struct._effectZ);
        if (!list)
            this.lists.set(struct._effectZ, list = new FastSinglelist());
        list.add(struct);
        if (list.length === 1)
            this._indice.add(struct._effectZ);
        return list;
    }
    reset() {
        this._indice.forEach(i => this.lists.get(i).clear());
        this._indice.clear();
        this._sortedIndice.length = 0;
    }
    get indice() {
        let arr = this._sortedIndice;
        if (arr.length === 0) {
            for (let zIndex of this._indice) {
                arr.push(zIndex);
            }
            arr.sort((a, b) => a - b);
        }
        return arr;
    }
    appendTo(out) {
        this.indice.forEach(zIndex => out.addList(this.lists.get(zIndex)));
    }
}
export class WebRender2DPass {
    get priority() {
        return this._priority;
    }
    set priority(value) {
        this._priority = value;
        if (this._mask && this._mask.pass) {
            this._mask.pass._priority = value + 1;
        }
    }
    get mask() {
        return this._mask;
    }
    set mask(value) {
        this._mask = value;
        if (value && value.pass) {
            value.pass.priority = this.priority + 1;
        }
    }
    get enableBatch() {
        return this._enableBatch;
    }
    set enableBatch(value) {
        this.repaint = true;
        this._enableBatch = value;
    }
    setClearColor(r, g, b, a) {
        this._clearColor.setValue(r, g, b, a);
    }
    constructor() {
        this._renderElements = new FastSinglelist();
        this._elementGroups = [];
        this._structs = new SortedStructs();
        this._structsPool = Pool.createPool(SortedStructs, null, obj => obj.reset());
        this._batchProviders = [];
        this._priority = 0;
        this.enable = true;
        this.isSupport = false;
        this.postProcess = null;
        this.repaint = true;
        this._clearColor = new Color;
        this.doClearColor = true;
        this.finalize = null;
        this._enableBatch = true;
        this._rtsize = new Vector2;
        this.root = null;
        this.offsetMatrix = new Matrix();
        this._invertMat_0 = new Vector3(1, 1);
        this._invertMat_1 = new Vector3(0, 0);
        this.shaderData = null;
        this.destroyed = false;
        this.shaderData = LayaGL.renderDeviceFactory.createShaderData(null);
    }
    needRender() {
        return this.enable
            && !this.isSupport
            && (this.repaint || !this.renderTexture);
    }
    cullAndSort(context2D, struct) {
        if (!struct.enabled)
            return;
        struct._handleInterData();
        if (struct._parentGlobalRenderData
            && (struct.renderLayer & struct._parentGlobalRenderData.renderLayerMask) === 0) {
            return;
        }
        struct.renderUpdate(context2D);
        let list = this._pStructs.add(struct);
        if (struct.stackingRoot) {
            var oldCol = this._pStructs;
            this._pStructs = this._structsPool.take();
        }
        for (let i = 0, n = struct.children.length; i < n; i++) {
            const child = struct.children[i];
            child._effectZ = child.zIndex + struct._effectZ;
            this.cullAndSort(context2D, child);
        }
        if (struct.dcOptimize) {
            let last = list.length - 1;
            struct.dcOptimizeEnd = list.elements[last];
        }
        if (oldCol) {
            this._pStructs.appendTo(list);
            this._structsPool.recover(this._pStructs);
            this._pStructs = oldCol;
        }
    }
    fowardRender(context) {
        var _a, _b;
        this._initRenderProcess(context);
        if (this.repaint) {
            this._structs.reset();
            this._renderElements.length = 0;
            for (let i = 0, n = this._batchProviders.length; i < n; i++) {
                (_a = this._batchProviders[i]) === null || _a === void 0 ? void 0 : _a.reset();
            }
            if (this.root) {
                this._pStructs = this._structs;
                this.cullAndSort(context, this.root);
                this.fillRenderElements();
                this._enableBatch && LayaEnv.isPlaying && this.batch();
            }
            WebRender2DPass.uploadBuffer();
            context.drawRenderElementList(this._renderElements);
            if (this._mask) {
                this._mask._handleInterData();
                this._mask.renderUpdate(context);
                context.drawRenderElementOne(this._mask.renderElements[0]);
            }
            if ((_b = this.postProcess) === null || _b === void 0 ? void 0 : _b.enabled) {
                this.postProcess._context.command.apply(true);
            }
        }
        else {
            this._structs.indice.forEach(index => {
                let list = this._structs.lists.get(index);
                for (let i = 0, cnt = list.length; i < cnt; i++) {
                    let struct = list.elements[i];
                    struct._handleInterData();
                    struct.renderUpdate(context);
                }
            });
            WebRender2DPass.uploadBuffer();
            context.drawRenderElementList(this._renderElements);
        }
        this.repaint = false;
    }
    fillRenderElements() {
        this._elementGroups.length = 0;
        let groupStart = 0;
        let reorderRoot;
        let renderElements = this._renderElements;
        this._structs.indice.forEach(index => {
            let list = this._structs.lists.get(index);
            for (let i = 0, cnt = list.length; i < cnt; i++) {
                let struct = list.elements[i];
                let n = struct.renderElements ? struct.renderElements.length : 0;
                if (struct.owner._getBit(NodeFlags.HIDE_BY_EDITOR))
                    n = 0;
                if (struct.dcOptimize && !reorderRoot && struct.dcOptimizeEnd !== struct) {
                    reorderRoot = struct;
                    if (groupStart !== renderElements.length) {
                        this._elementGroups.push(groupStart, renderElements.length - 1, false);
                        groupStart = renderElements.length;
                    }
                }
                if (n > 0) {
                    if (reorderRoot != null && struct.dcBoundsTarget != reorderRoot) {
                        struct.dcBoundsTarget = reorderRoot;
                        let rect = struct.owner.getSelfBounds(struct.dcBounds, false);
                        SpriteUtils.transformRect(struct.owner, rect, reorderRoot.owner, rect);
                    }
                    for (let i = 0; i < n; i++) {
                        let element = struct.renderElements[i];
                        element._index = i;
                        renderElements.add(element);
                    }
                }
                if ((reorderRoot === null || reorderRoot === void 0 ? void 0 : reorderRoot.dcOptimizeEnd) === struct) {
                    reorderRoot = null;
                    if (groupStart !== renderElements.length) {
                        this._elementGroups.push(groupStart, renderElements.length - 1, true);
                        groupStart = renderElements.length;
                    }
                }
            }
        });
        if (groupStart !== renderElements.length)
            this._elementGroups.push(groupStart, renderElements.length - 1, false);
    }
    batch() {
        let list = this._renderElements;
        let length = list.length;
        let elementArray = list.elements;
        let groups = this._elementGroups;
        list.length = 0;
        for (let gi = 0, gl = groups.length; gi < gl; gi += 3) {
            let groupStart = groups[gi];
            let groupEnd = groups[gi + 1];
            let allowReorder = groups[gi + 2];
            let lastRenderType = elementArray[groupStart].owner.renderType;
            let batchStart = groupStart;
            for (let i = groupStart + 1; i <= groupEnd; i++) {
                let element = elementArray[i];
                let struct = element.owner;
                if (lastRenderType === struct.renderType)
                    continue;
                if (allowReorder) {
                    for (let j = i + 1; j <= groupEnd; j++) {
                        let element2 = elementArray[j];
                        if (element2.owner.renderType === lastRenderType) {
                            for (let k = j - 1; k >= i; k--) {
                                if (element2.owner.dcBounds.intersects(elementArray[k].owner.dcBounds)) {
                                    element2 = null;
                                    break;
                                }
                            }
                            if (element2 != null) {
                                elementArray.splice(j, 1);
                                elementArray.splice(i, 0, element2);
                                element = elementArray[++i];
                            }
                        }
                    }
                }
                if (i - batchStart > 1)
                    this.getBatchProvider(lastRenderType).batch(list, batchStart, i - 1, allowReorder);
                else
                    list.add(elementArray[batchStart]);
                batchStart = i;
                lastRenderType = struct.renderType;
            }
            if (groupEnd - batchStart > 0)
                this.getBatchProvider(lastRenderType).batch(list, batchStart, groupEnd, allowReorder);
            else
                list.add(elementArray[batchStart]);
        }
    }
    getBatchProvider(renderType) {
        return this._batchProviders[renderType] || (this._batchProviders[renderType] = BatchManager.createProvider(renderType));
    }
    _initRenderProcess(context) {
        let sizeX, sizeY;
        let rt = this.renderTexture;
        if (rt) {
            context.invertY = rt._invertY;
            context.setRenderTarget(rt._renderTarget, this.doClearColor, this._clearColor);
            sizeX = rt.width;
            sizeY = rt.height;
            this._updateInvertMatrix();
            this.shaderData.addDefine(ShaderDefines2D.RENDERTEXTURE);
        }
        else {
            context.invertY = false;
            sizeX = RenderState2D.width;
            sizeY = RenderState2D.height;
            context.setOffscreenView(sizeX, sizeY);
            context.setRenderTarget(null, this.doClearColor, this._clearColor);
            this._setInvertMatrix(1, 0, 0, 1, 0, 0);
            this.shaderData.removeDefine(ShaderDefines2D.RENDERTEXTURE);
        }
        context.passData = this.shaderData;
        if (sizeX !== this._rtsize.x || sizeY !== this._rtsize.y) {
            this._rtsize.setValue(sizeX, sizeY);
            this.shaderData.setVector2(ShaderDefines2D.UNIFORM_SIZE, this._rtsize);
        }
    }
    static setBuffer(buffer) {
        if (buffer._inPass)
            return;
        buffer._inPass = true;
        WebRender2DPass.buffers.add(buffer);
    }
    static uploadBuffer() {
        if (WebRender2DPass.buffers.size > 0) {
            WebRender2DPass.buffers.forEach(buffer => {
                buffer._upload();
                buffer._inPass = false;
            });
            WebRender2DPass.buffers.clear();
        }
    }
    _updateInvertMatrix() {
        let rootTrans = this.root.trans;
        if (!rootTrans)
            return this._setInvertMatrix(1, 0, 0, 1, 0, 0);
        let temp = _TEMP_InvertMatrix;
        let mask = this.mask;
        let offset = this.offsetMatrix;
        if (mask && mask.trans) {
            let maskMatrix = mask.trans.matrix;
            maskMatrix.copyTo(temp);
        }
        else {
            rootTrans.matrix.copyTo(temp);
        }
        Matrix.mul(offset, temp, temp);
        temp.invert();
        this._setInvertMatrix(temp.a, temp.b, temp.c, temp.d, temp.tx, temp.ty);
    }
    _setInvertMatrix(a = 1, b = 0, c = 0, d = 1, tx = 0, ty = 0) {
        if (a === this._invertMat_0.x
            && b === this._invertMat_1.x
            && c === this._invertMat_0.y
            && d === this._invertMat_1.y
            && tx === this._invertMat_0.z
            && ty === this._invertMat_1.z)
            return;
        this._invertMat_0.setValue(a, c, tx);
        this._invertMat_1.setValue(b, d, ty);
        this.shaderData.setVector3(ShaderDefines2D.UNIFORM_INVERTMAT_0, this._invertMat_0);
        this.shaderData.setVector3(ShaderDefines2D.UNIFORM_INVERTMAT_1, this._invertMat_1);
    }
    destroy() {
        if (this.destroyed) {
            return;
        }
        this.destroyed = true;
        this._renderElements.length = 0;
        for (let i = 0, n = this._batchProviders.length; i < n; i++) {
            this._batchProviders[i] && this._batchProviders[i].destroy();
        }
        this._batchProviders.length = 0;
        this.root = null;
        this.renderTexture = null;
        this.postProcess = null;
        this.shaderData.destroy();
        this.shaderData = null;
    }
}
WebRender2DPass.buffers = new Set();
export class WebRender2DPassManager {
    constructor() {
        this._modify = false;
        this._passes = [];
    }
    removePass(pass) {
        let index = this._passes.indexOf(pass);
        if (index === -1) {
            return;
        }
        this._passes.splice(index, 1);
        this._modify = true;
    }
    apply(context) {
        if (this._modify) {
            this._modify = false;
            this._passes.sort((a, b) => b._priority - a._priority);
        }
        for (const pass of this._passes) {
            if (pass.needRender()) {
                pass.fowardRender(context);
            }
        }
    }
    clear() {
        this._passes.length = 0;
    }
    addPass(pass) {
        if (this._passes.indexOf(pass) !== -1) {
            return;
        }
        this._passes.push(pass);
        this._modify = true;
    }
}
const _TEMP_InvertMatrix = new Matrix();
